import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Phone, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable list section
const ConsentSection = ({ title, titleColor, items, itemColor = '#1A1A2E', markerColor }) => (
  <div className="mb-10 animate-slideUp">
    <h2 className="text-sm uppercase tracking-widest mb-4" style={{ color: titleColor }}>
      {title}
    </h2>
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3" style={{ color: itemColor }}>
          <span style={{ color: markerColor || titleColor }}>✦</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

// Modal component
const LegalModal = ({ show, onClose, title, children }) => {
  if (!show) return null;
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn"
      style={{ backgroundColor: 'rgba(8, 8, 16, 0.95)' }}
    >
      <div 
        className="w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl p-8"
        style={{ backgroundColor: '#FFFFFF', border: '1px solid #EAE4DC' }}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-medium" style={{ color: '#C9A84C' }}>{title}</h2>
          <button onClick={onClose} className="text-[#6B6878] hover:text-[#1A1A2E] transition-colors" data-testid="modal-close">
            <X className="w-6 h-6" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const ConsentPage = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const scrollContainerRef = useRef(null);

  // Handle scroll tracking
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setHasScrolledToBottom(true);
      }
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Redirect if already consented
  useEffect(() => {
    if (user?.consent_accepted) {
      navigate(user?.onboarding_complete ? '/sanctuary' : '/assessment');
    }
  }, [user, navigate]);

  const handleAccept = useCallback(async () => {
    if (!hasScrolledToBottom) {
      toast.error('Please scroll to read the full agreement');
      return;
    }
    if (!ageConfirmed) {
      toast.error('Please confirm that you are 18 years of age or older');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/accept-consent`,
        { consent_version: "1.0", age_confirmed: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      navigate('/assessment');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record consent');
    } finally {
      setLoading(false);
    }
  }, [hasScrolledToBottom, ageConfirmed, token, refreshUser, navigate]);

  const handleCrisisSupport = useCallback(() => {
    window.open('https://988lifeline.org/chat/', '_blank');
  }, []);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FBF9F6', fontFamily: 'Jost, sans-serif' }}>
      {/* Scrollable content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-6 py-12">
        <div className="max-w-xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 animate-slideUp">
            <h1 className="text-4xl md:text-5xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#C9A84C' }}>
              Before We Begin
            </h1>
            <p className="text-lg md:text-xl leading-relaxed" style={{ color: '#4A4A5A' }}>
              This is your safe space. We want you to know exactly what Mindful Renewal is — and isn't.
            </p>
          </div>

          {/* What Sage Is */}
          <ConsentSection
            title="What Sage Is"
            titleColor="#C9A84C"
            items={[
              'A trauma-informed AI healing companion',
              'A safe, private space for reflection and growth',
              'A tool to support your emotional wellness journey'
            ]}
          />

          {/* What Sage Is Not */}
          <ConsentSection
            title="What Sage Is Not"
            titleColor="#e8b4b8"
            items={[
              'A licensed therapist or mental health professional',
              'A crisis intervention service',
              'A replacement for professional treatment'
            ]}
          />

          {/* Your Data */}
          <ConsentSection
            title="Your Data"
            titleColor="#6B6878"
            itemColor="#4A4A5A"
            markerColor="#8A8798"
            items={[
              'Your journal entries are private and encrypted',
              'We never sell your personal data',
              'You can delete your data at any time'
            ]}
          />

          {/* Crisis Resources */}
          <div 
            className="mb-10 p-6 rounded-xl animate-slideUp"
            style={{ backgroundColor: 'rgba(232, 180, 184, 0.1)', border: '1px solid rgba(232, 180, 184, 0.2)' }}
          >
            <h2 className="text-sm uppercase tracking-widest mb-4" style={{ color: '#A9756B' }}>
              If You Are In Crisis
            </h2>
            <div className="space-y-2" style={{ color: '#1A1A2E' }}>
              <p className="text-sm mb-3" style={{ color: '#4A4A5A' }}>
                The following resources are available in the United States:
              </p>
              <p><strong style={{ color: '#A9756B' }}>Crisis Text Line:</strong> Text HOME to 741741</p>
              <p><strong style={{ color: '#A9756B' }}>988 Suicide & Crisis Lifeline:</strong> Call or text 988</p>
              <p><strong style={{ color: '#A9756B' }}>Emergency Services:</strong> 911</p>
            </div>
          </div>

          {/* Agreement */}
          <div className="mb-8 animate-slideUp">
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#4A4A5A' }}>
              By tapping <strong style={{ color: '#C9A84C' }}>I Understand & Agree</strong> you confirm:
            </p>
            <ul className="space-y-2 text-sm mb-6" style={{ color: '#4A4A5A' }}>
              <li>— You understand Sage is an AI companion, not a therapist</li>
              <li>
                — You have read and agree to our{' '}
                <button onClick={() => setShowTermsModal(true)} className="underline hover:no-underline" style={{ color: '#C9A84C' }}>
                  Terms of Service
                </button>
                {' '}and{' '}
                <button onClick={() => setShowPrivacyModal(true)} className="underline hover:no-underline" style={{ color: '#C9A84C' }}>
                  Privacy Policy
                </button>
              </li>
            </ul>

            {/* Age Confirmation */}
            <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(201, 168, 76, 0.1)', border: '1px solid rgba(201, 168, 76, 0.3)' }}>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={ageConfirmed}
                  onCheckedChange={setAgeConfirmed}
                  className="mt-0.5 border-[#C9A84C] data-[state=checked]:bg-[#C9A84C]"
                  data-testid="age-confirm-checkbox"
                />
                <span className="text-sm leading-relaxed" style={{ color: '#1A1A2E' }}>
                  <strong style={{ color: '#C9A84C' }}>I confirm I am 18 years of age or older</strong>
                  <br />
                  <span style={{ color: '#4A4A5A' }}>This service is designed for adults only.</span>
                </span>
              </label>
            </div>
          </div>

          {/* Scroll indicator */}
          {!hasScrolledToBottom && (
            <div className="text-center py-4 animate-pulse" style={{ color: '#8A8798' }}>
              <p className="text-sm">↓ Scroll to continue ↓</p>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Buttons */}
      <div className="p-6 border-t" style={{ backgroundColor: '#FBF9F6', borderColor: '#EAE4DC' }}>
        <div className="max-w-xl mx-auto space-y-3">
          <Button
            onClick={handleAccept}
            disabled={loading || !hasScrolledToBottom || !ageConfirmed}
            className={`w-full rounded-full py-6 text-lg font-medium transition-all ${hasScrolledToBottom && ageConfirmed ? 'opacity-100' : 'opacity-50 cursor-not-allowed'}`}
            style={{ backgroundColor: (hasScrolledToBottom && ageConfirmed) ? '#C9A84C' : 'rgba(201, 168, 76, 0.5)', color: '#FBF9F6' }}
            data-testid="consent-accept-button"
          >
            {loading ? 'Processing...' : 'I Understand & Agree'}
          </Button>
          
          <Button
            onClick={handleCrisisSupport}
            variant="outline"
            className="w-full rounded-full py-6 text-lg font-medium"
            style={{ backgroundColor: 'transparent', borderColor: '#e8b4b8', color: '#A9756B' }}
            data-testid="crisis-support-button"
          >
            <Phone className="w-5 h-5 mr-2" />
            I Need Crisis Support Now
          </Button>
        </div>
      </div>

      {/* Privacy Modal */}
      <LegalModal show={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} title="Privacy Policy">
        <div className="text-[#4A4A5A] text-sm space-y-4">
          <p>Your privacy is important to us. Here's how we protect your data:</p>
          <ul className="space-y-2 list-disc list-inside">
            <li>All conversations are encrypted end-to-end</li>
            <li>We never sell your personal information</li>
            <li>You can request deletion of your data at any time</li>
            <li>We use Claude AI, which does not train on your conversations</li>
          </ul>
          <p>For the full privacy policy, please visit our website.</p>
        </div>
      </LegalModal>

      {/* Terms Modal */}
      <LegalModal show={showTermsModal} onClose={() => setShowTermsModal(false)} title="Terms of Service">
        <div className="text-[#4A4A5A] text-sm space-y-4">
          <p>By using Mindful Renewal, you agree to the following terms:</p>
          <ul className="space-y-2 list-disc list-inside">
            <li>Sage is an AI companion, not a licensed therapist</li>
            <li>You must be 18 years or older to use this service</li>
            <li>If you're in crisis, please contact emergency services</li>
            <li>We reserve the right to modify services with notice</li>
          </ul>
          <p>For the full terms of service, please visit our website.</p>
        </div>
      </LegalModal>
    </div>
  );
};

export default ConsentPage;
